import { useOutletContext } from 'react-router-dom'

import { Button, List, Section, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { ExpensesEntitiesExpensableStatusEnum } from 'generated/resources/graphql'
import moment from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useGetExpensesCount from 'modules/expenses/hooks/useGetExpensesCount'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'

import { useQuickFiltersContext } from './QuickFiltersContext'

interface Props {
  title: string
  filterType: ExpensesEntitiesExpensableStatusEnum
}

const Header = ({ title, filterType }: Props) => {
  const pendingApproval = filterType === 'pending' ? 'mine' : undefined
  const { section } = useOutletContext<RouteContextParams>()
  const { expenseCount, loading: loadingExpensesCount } = useGetExpensesCount({
    status: filterType,
    pendingApproval,
  })
  const { filters, setFilters, clearFilters } = useQuickFiltersContext()
  const { setPageDateFilters } = usePageDateFilters()
  const hasAppliedFilter = !!filters?.status

  useEffect(() => {
    if (hasAppliedFilter && expenseCount == 0) {
      clearExpensesFilter()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseCount, hasAppliedFilter])

  const trackQuickFilter = () => {
    track(trackingEvents.expenses.quickFilterExpensesStatus, {
      status: filterType,
      count: expenseCount,
      context: section,
    })
  }

  const setExpensesFilter = () => {
    trackQuickFilter()
    setFilters({
      status: filterType,
      pendingApproval,
    })
    setPageDateFilters(moment().subtract(364, 'days').toDate(), new Date())
  }

  const clearExpensesFilter = () => {
    clearFilters()
  }

  return (
    <Section title={''} description={''} size="600" paddingX="s0" paddingY="s0">
      {loadingExpensesCount ? (
        <Loader />
      ) : (
        <QuickLink
          expenseCount={expenseCount}
          filterType={filterType}
          hasAppliedFilter={hasAppliedFilter}
          onApply={setExpensesFilter}
          onClear={clearExpensesFilter}
        />
      )}
    </Section>
  )
}

const Loader = () => (
  <List>
    <List.ClickableItem>
      <List.Title>
        <Skeleton width="s200" height="s12" />
      </List.Title>

      <List.ActionItem>
        <Skeleton width="s32" height="s32" />
      </List.ActionItem>
    </List.ClickableItem>
  </List>
)

interface QuickLinkProps {
  expenseCount?: number | null
  filterType: ExpensesEntitiesExpensableStatusEnum
  hasAppliedFilter: boolean
  onClear: () => void
  onApply: () => void
}

const getDraftExpensesCount = (count: number) => {
  return i18n.tp('expenses_v2.banners.draft_expenses_count', { count })
}

const getPendingExpensesCount = (count: number) => {
  return i18n.tp('expenses_v2.banners.pending_expenses_count', { count })
}

const QuickLink = ({
  expenseCount,
  filterType,
  hasAppliedFilter,
  onClear,
  onApply,
}: QuickLinkProps) => {
  if (!expenseCount) return null

  return (
    <List>
      <List.Item>
        <List.Title>
          <Text size="300" weight="medium">
            {filterType === 'draft'
              ? getDraftExpensesCount(expenseCount)
              : getPendingExpensesCount(expenseCount)}
          </Text>
        </List.Title>
        <List.Description>
          {hasAppliedFilter ? (
            <Button type="secondary" onClick={onClear} iconName="cross" />
          ) : (
            <Button type="secondary" onClick={onApply} iconName="arrow-right" />
          )}
        </List.Description>
      </List.Item>
    </List>
  )
}

export default Header
